//#region creators
/**
 * Iterate over numbers between start (inclusive) and end (exclusive), with a step of step
 *
 * @category creators (sync)
 * @see {@link https://docs.python.org/3/library/itertools.html#itertools.count | `itertools.count` in python}
 */
export function* count(
/**
 * The beginning of the range, inclusive
 *
 * @defaultValue 0
 */
start = 0, 
/**
 * The step between two numbers
 *
 * @defaultValue 1
 */
step = 1, 
/**
 * The end of the range, exclusive
 *
 * If step is positive, this should be greater than start, otherwise lesser than start
 *
 * @defaultValue +/- Infinity
 */
end = step > 0 ? Infinity : -Infinity) {
    for (let i = start; step > 0 ? i < end : i > end; i += step)
        yield i;
}
/**
 * Repeat a given element a given amount of times or infinitely
 *
 * @category creators (sync)
 * @see {@link https://docs.python.org/3/library/itertools.html#itertools.repeat | `itertools.repeat` in python}
 */
export function* repeat(
/**
 * The element to repeat
 */
e, 
/**
 * How many times to repeat the element
 *
 * @defaultValue Infinity
 */
times) {
    if (times != null) {
        for (let i = 0; i < times; i++)
            yield e;
    }
    else {
        for (;;)
            yield e;
    }
}
/**
 * Iterate over the entries of a plain object
 *
 * @category creators (sync)
 * @see {@link https://www.lua.org/manual/5.4/manual.html#pdf-pairs | `pairs()` in lua}
 */
export function pairs(
/**
 * The plain object to iterate over
 */
obj) {
    return Object.entries(obj);
}
/**
 * Iterate over the index and value of an array
 *
 * @category creators (sync)
 * @see {@link https://www.lua.org/manual/5.4/manual.html#pdf-ipairs | `ipairs()` in lua}
 */
export function* ipairs(
/**
 * The array to iterate over
 */
arr) {
    const len = arr.length;
    for (let i = 0; i < len; i++) {
        // eslint-disable-next-line security/detect-object-injection
        yield [i, arr[i]];
    }
}
/**
 * Iterate over an iterator
 *
 * @category creators (sync)
 */
export function* iter(
/**
 * The iterator to iterate over
 */
it) {
    for (;;) {
        const e = it.next();
        if (e.done)
            return;
        yield e.value;
    }
}
//#endregion creators
//#region transforms
/**
 * Iterate over another iterable, separating every value with a given separator
 *
 * @category transformers (sync)
 * @see {@link https://doc.rust-lang.org/std/iter/trait.Iterator.html#method.intersperse | `Iter.intersperse` in rust}
 */
export function* separate(
/**
 * The iterable to iterate over
 */
it, 
/**
 * The separator to injet between elements
 */
separator) {
    let first = true;
    for (const x of it) {
        if (!first)
            yield separator;
        yield x;
        first = false;
    }
}
/**
 * Iterates over an iterable, returning tuples with one value and its position in the iteration
 *
 * Be careful, the order is [value, position], unlike the python `enumerate()` function which returns `(postition, value)`
 *
 * @category transformers (sync)
 * @see {@link https://docs.python.org/3.12/library/functions.html#enumerate | `enumerate()` in python}
 */
export function* enumerate(
/**
 * The iterable to iterate over
 */
iter) {
    let i = 0;
    for (const o of iter)
        yield [o, i++];
}
export function* filter(
/**
 * The source iterable
 */
iter, 
/**
 * The predicate to use
 */
pred) {
    for (const val of iter) {
        if (pred(val))
            yield val;
    }
}
export function* takeWhile(
/**
 * The source iterable
 */
iter, 
/**
 * The predicate to use
 */
pred) {
    for (const val of iter) {
        if (!pred(val))
            return;
        yield val;
    }
}
/**
 * Returns a new iterable containing every value of the given iterable, starting when the predicate returns false for the first time
 *
 * @category transformers (sync)
 * @see {@link https://doc.rust-lang.org/std/iter/trait.Iterator.html#method.skip_while | `Iter.skip_while` in rust}
 */
export function* skipWhile(
/**
 * The source iterable
 */
iter, 
/**
 * The predicate to use
 */
pred) {
    let active = false;
    for (const val of iter) {
        if (active || !pred(val)) {
            active = true;
            yield val;
        }
    }
}
/**
 * Returns a new iterable containing the mapped values of the given iterable, by the given transform
 *
 * @category transformers (sync)
 * @see {@link https://doc.rust-lang.org/std/iter/trait.Iterator.html#method.map | `Iter.map` in rust}
 */
export function* map(
/**
 * The source iterable
 */
iter, 
/**
 * The transform to use
 */
transform) {
    for (const val of iter)
        yield transform(val);
}
/**
 * Returns a new iterable containing the successive state values (except the initial one, unlike the python version) of the given reducer, applied to the given iterable
 *
 * @category transformers (sync)
 * @see {@link https://docs.python.org/3/library/itertools.html#itertools.accumulate | `itertools.accumulate` in python}
 * @see {@link reduce}
 */
export function* reduced(
/**
 * The source iterable
 */
iter, 
/**
 * The reducer to use
 */
reducer, 
/**
 * Initial reducer state
 */
initial) {
    let state = initial;
    for (const val of iter) {
        state = reducer(val, state);
        yield state;
    }
}
/**
 * Returns a new iterable containing the mapped values (from a stateful map function) of the given iterable by the given map function
 *
 * @category transformers (sync)
 */
export function* statefulMap(
/**
 * The source iterable
 */
iter, 
/**
 * The stateful map function
 */
reducer, 
/**
 * Initial state
 */
initial) {
    let state = initial;
    for (const val of iter) {
        const current = reducer(val, state);
        state = current[0];
        yield current[1];
    }
}
/**
 * Repeat an iterable forever
 *
 * @category transformers (sync)
 * @see {@link https://doc.rust-lang.org/std/iter/trait.Iterator.html#method.cycle | `Iter.cycle` in rust}
 */
export function* cycle(iter) {
    const tab = [];
    for (const val of iter) {
        tab.push(val);
        yield val;
    }
    if (!tab.length)
        return;
    for (;;) {
        for (const val of tab)
            yield val;
    }
}
/**
 * Repeat an iterable a given amount of times
 *
 * @category transformers (sync)
 * @see {@link cycle}
 */
export function* cycleN(iter, n) {
    if (!n)
        return;
    if (n === 1)
        return iter;
    const tab = [];
    for (const val of iter) {
        tab.push(val);
        yield val;
    }
    n--;
    while (n) {
        for (const val of tab)
            yield val;
        n--;
    }
}
/**
 * Return an iterable containing a specified amount of items (at maximum) from a source iterable
 *
 * @category transformers (sync)
 * @see {@link https://doc.rust-lang.org/std/iter/trait.Iterator.html#method.take | `Iter.take` in rust}
 */
export function* take(iter, n) {
    if (n <= 0)
        return;
    let i = 0;
    for (const val of iter) {
        yield val;
        i++;
        if (i >= n)
            return;
    }
}
/**
 * Return an iterable containing all but a specified amount of items from a source iterable
 *
 * @category transformers (sync)
 * @see {@link https://doc.rust-lang.org/std/iter/trait.Iterator.html#method.skip | `Iter.skip` in rust}
 */
export function* skip(iter, n) {
    if (n <= 0)
        return iter;
    const it = iter[Symbol.iterator]();
    for (let i = 0; i < n; i++) {
        const val = it.next();
        if (val.done)
            return;
    }
    for (;;) {
        const val = it.next();
        if (val.done)
            return;
        yield val.value;
    }
}
/**
 * Return an iterable batching the items from the source iterable into blocks of n items
 *
 * The last block might be smaller if the total item count is not a multiple of n (unlike the rust implementation that exposes them differently, but like the python implementation)
 *
 * @category transformers (sync)
 * @see {@link https://docs.python.org/3/library/itertools.html#itertools.batched | `itertools.batched` in python}
 * @see {@link https://doc.rust-lang.org/std/iter/trait.Iterator.html#method.array_chunks | `Iter.array_chunks` in rust}
 */
export function* batch(iter, n) {
    let batch = [];
    let l = 0;
    for (const e of iter) {
        batch.push(e);
        l++;
        if (l === n) {
            yield batch;
            batch = [];
            l = 0;
        }
    }
    if (l)
        yield batch;
}
/**
 * Returns an iterable containing windows around each element of this iterable, with the given amount of pre- and post- elements
 *
 * @category transformers (sync)
 * @see {@link https://more-itertools.readthedocs.io/en/stable/api.html#more_itertools.windowed | `more_itertools.windowed` in python}
 */
export function* window(
/**
 * The source iterable
 */
iter, 
/**
 * How many elements to return in the pre- part
 */
beforeCount, 
/**
 * How many elements to return in the post- part
 */
afterCount) {
    const before = [];
    // dude trust me
    let current = void 0;
    const after = [];
    let beforeLen = 0;
    let afterLen = 0;
    let hasCurrent = false;
    for (const e of iter) {
        after.push(e);
        afterLen++;
        if (afterLen > afterCount) {
            if (hasCurrent) {
                before.push(current);
                beforeLen++;
            }
            if (beforeLen > beforeCount) {
                before.shift();
                beforeLen--;
            }
            current = after.shift();
            afterLen--;
            hasCurrent = true;
            yield [[...before], current, [...after]];
        }
    }
    while (afterLen) {
        if (hasCurrent) {
            before.push(current);
            beforeLen++;
        }
        if (beforeLen > beforeCount) {
            before.shift();
            beforeLen--;
        }
        current = after.shift();
        afterLen--;
        hasCurrent = true;
        yield [[...before], current, [...after]];
    }
}
/**
 * Return an iterable containing the values of every iterable returned by calling the map on the provided iterable
 *
 * @category transformers (sync)
 * @see {@link https://doc.rust-lang.org/std/iter/trait.Iterator.html#method.flat_map | `Iter.flat_map` in rust}
 */
export function* flatMap(
/**
 * The source iterable
 */
iter, 
/**
 * The transform function, returning iterables
 */
transform) {
    for (const it of iter) {
        for (const val of transform(it)) {
            yield val;
        }
    }
}
/**
 * Deduplicate an iterator using a {@link Set}
 *
 * @category transformers (sync)
 */
export function* dedup(
/**
 * The iterable to dedupliate
 */
iter) {
    const seen = new Set();
    for (const item of iter) {
        if (seen.has(item))
            continue;
        seen.add(item);
        yield item;
    }
}
export function* dedupBy(iter, mapOrKey) {
    const seen = new Set();
    if (typeof mapOrKey === 'function') {
        for (const item of iter) {
            const mapped = mapOrKey(item);
            if (seen.has(mapped))
                continue;
            seen.add(mapped);
            yield item;
        }
    }
    else {
        for (const item of iter) {
            // eslint-disable-next-line security/detect-object-injection
            const key = item[mapOrKey];
            if (seen.has(key))
                continue;
            seen.add(key);
            yield item;
        }
    }
}
//#endregion transforms
//#region aggregators
/**
 * Iterate over every iterable in the list, retuning a value from the first, then the second, etc. then over again
 *
 * Finishes when any iterable finishes
 *
 * @category aggregators (sync)
 * @see {@link https://more-itertools.readthedocs.io/en/stable/api.html#more_itertools.interleave | `more_itertools.interleave` in python}
 */
export function* intersperse(
/**
 * The iterables to intersperse
 */
...iters) {
    const it = iters.map(x => x[Symbol.iterator]());
    const e = Array.from({ length: it.length });
    for (;;) {
        for (const [i, element] of it.entries()) {
            // eslint-disable-next-line security/detect-object-injection
            e[i] = element.next();
            // eslint-disable-next-line security/detect-object-injection
            if (e[i].done)
                return;
        }
        for (const x of e)
            yield x.value;
    }
}
/**
 * Zip together multiple iterables, returning tuples with one value from each iterable
 *
 * Finishes when any iterable finishes
 *
 * @category aggregators (sync)
 * @see {@link https://docs.python.org/3.12/library/functions.html#zip | `zip()` in python}
 */
export function* zip(...iters) {
    const it = iters.map(x => x[Symbol.iterator]());
    const e = Array.from({ length: it.length });
    for (;;) {
        for (const [i, element] of it.entries()) {
            // eslint-disable-next-line security/detect-object-injection
            e[i] = element.next();
            // eslint-disable-next-line security/detect-object-injection
            if (e[i].done)
                return;
        }
        yield e.map(x => x.value);
    }
}
/**
 * Returns a new iterable containing the values of each iterable, one iterable after the other
 *
 * Version with a vararg of iterables
 *
 * @category aggregators (sync)
 * @see {@link chainIt}
 * @see {@link https://doc.rust-lang.org/std/iter/trait.Iterator.html#method.chain | `Iter.chain` in rust}
 */
export function* chain(...iters) {
    for (const iter of iters)
        for (const val of iter)
            yield val;
}
/**
 * Returns a new iterable containing the values of each iterable, one iterable after the other
 *
 * Version with an iterable of iterables
 *
 * @category aggregators (sync)
 * @see {@link chain}
 * @see {@link https://doc.rust-lang.org/std/iter/trait.Iterator.html#method.chain | `Iter.chain` in rust}
 */
export function* chainIt(iters) {
    for (const iter of iters)
        for (const val of iter)
            yield val;
}
//#endregion aggregators
//#region peeking
/**
 * Read the first n elements (at most) ofc the provided iterable, and return them along with an iterable that still contains them
 *
 * @category peeking (sync)
 * @see {@link peekable}
 * @see {@link https://more-itertools.readthedocs.io/en/stable/api.html#more_itertools.spy | `more_itertools.spy` in python}
 */
export function peek(
/**
 * The iterable to peek at
 */
iterable, 
/**
 * How many elements to peek
 *
 * @defaultValue 1
 */
n = 1) {
    if (n === 0)
        return [[], iterable];
    const it = iterable[Symbol.iterator]();
    const peeked = [];
    for (let i = 0; i < n; i++) {
        const e = it.next();
        if (e.done) {
            return [peeked, peeked];
        }
        peeked.push(e.value);
    }
    return [peeked, chain(peeked, iter(it))];
}
/**
 * Returns an iterable that can be peeked (reading the top element without advancing), from a given iterable
 *
 * @category peeking (sync)
 * @see {@link https://doc.rust-lang.org/std/iter/trait.Iterator.html#method.peekable | `Iter.peekable` in rust}
 */
export function peekable(
/**
 * The source iterable
 */
iter) {
    // dude trust me
    let state = [void 0, false];
    const it = iter[Symbol.iterator]();
    function advance() {
        if (state[1])
            return;
        const e = it.next();
        if (e.done)
            state = [null, true];
        else
            state = [e.value, false];
    }
    advance();
    return {
        [Symbol.iterator]() {
            return {
                next() {
                    if (state[1])
                        return { done: true, value: void 0 };
                    const value = state[0];
                    advance();
                    return { value };
                },
            };
        },
        get done() {
            return state[1];
        },
        peek() {
            if (state[1])
                throw new Error('Iterable ended');
            return state[0];
        },
    };
}
//#endregion peeking
//#region collection
/**
 * Reduce an iterable down to a single value, with the given reducer
 *
 * @category collection (sync)
 * @see {@link https://doc.rust-lang.org/std/iter/trait.Iterator.html#method.fold | `Iter.fold` in rust}
 */
export function reduce(
/**
 * The iterable to reduce
 */
iter, 
/**
 * The reducer to use
 */
reducer, 
/**
 * Initial state for the reducer
 */
initial) {
    let state = initial;
    for (const val of iter)
        state = reducer(val, state);
    return state;
}
export function find(
/**
 * The iterable to find the value in
 */
iter, 
/**
 * The predicate to find the value
 */
predicate, 
/**
 * The value to return if the value is not found
 */
notFound) {
    for (const val of iter)
        if (predicate(val))
            return val;
    return notFound;
}
/**
 * Iterates over the iterable and returns true (and stop iterating) if any value matches the predicate
 *
 * @category collection (sync)
 * @see {@link every}
 */
export function some(
/**
 * The iterable to find the value in
 */
iter, 
/**
 * The predicate that should match
 */
predicate) {
    for (const val of iter)
        if (predicate(val))
            return true;
    return false;
}
/**
 * Iterates over the iterable and returns false (and stop iterating) if any value doesn't match the predicate
 *
 * @category collection (sync)
 * @see {@link every}
 */
export function every(
/**
 * The iterable to find the value in
 */
iter, 
/**
 * The predicate that should match
 */
predicate) {
    for (const val of iter)
        if (!predicate(val))
            return false;
    return true;
}
/**
 * Iterate over the entire iterable and return the number of items
 *
 * @category collection (sync)
 * @see {@link https://more-itertools.readthedocs.io/en/stable/api.html#more_itertools.ilen | `more_itertools.ilen` in python}
 */
export function length(
/**
 * The iterable to iterate over
 */
iter) {
    if (Array.isArray(iter))
        return iter.length;
    let c = 0;
    for (const _ of iter) {
        void _;
        c++;
    }
    return c;
}
/**
 * Iterate over the entire iterable and run a function for every item
 *
 * @category collection (sync)
 */
export function forEach(
/**
 * The iterable to iterate over
 */
iter, 
/**
 * The function to run on every item
 */
fn) {
    for (const val of iter)
        fn(val);
}
/**
 * Iterate over the entire iterable and collect the values into an array
 *
 * @category collection (sync)
 */
export function collect(
/**
 * The iterable to collect
 */
iter) {
    return [...iter];
}
//#endregion collection
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoiaXRlci5qcyIsInNvdXJjZVJvb3QiOiJzcmMvIiwic291cmNlcyI6WyJpdGVyLnRzIl0sIm5hbWVzIjpbXSwibWFwcGluZ3MiOiJBQUFBLGtCQUFrQjtBQUVsQjs7Ozs7R0FLRztBQUNILE1BQU0sU0FBUyxDQUFDLENBQUMsS0FBSztBQUNyQjs7OztHQUlHO0FBQ0gsS0FBSyxHQUFHLENBQUM7QUFDVDs7OztHQUlHO0FBQ0gsSUFBSSxHQUFHLENBQUM7QUFDUjs7Ozs7O0dBTUc7QUFDSCxHQUFHLEdBQUcsSUFBSSxHQUFHLENBQUMsQ0FBQyxDQUFDLENBQUMsUUFBUSxDQUFDLENBQUMsQ0FBQyxDQUFDLFFBQVE7SUFFckMsS0FBSyxJQUFJLENBQUMsR0FBRyxLQUFLLEVBQUUsSUFBSSxHQUFHLENBQUMsQ0FBQyxDQUFDLENBQUMsQ0FBQyxHQUFHLEdBQUcsQ0FBQyxDQUFDLENBQUMsQ0FBQyxHQUFHLEdBQUcsRUFBRSxDQUFDLElBQUksSUFBSTtRQUFFLE1BQU0sQ0FBQyxDQUFBO0FBQ3JFLENBQUM7QUFFRDs7Ozs7R0FLRztBQUNILE1BQU0sU0FBUyxDQUFDLENBQUMsTUFBTTtBQUN0Qjs7R0FFRztBQUNILENBQUk7QUFDSjs7OztHQUlHO0FBQ0gsS0FBYztJQUVkLElBQUksS0FBSyxJQUFJLElBQUksRUFBRSxDQUFDO1FBQ25CLEtBQUssSUFBSSxDQUFDLEdBQUcsQ0FBQyxFQUFFLENBQUMsR0FBRyxLQUFLLEVBQUUsQ0FBQyxFQUFFO1lBQUUsTUFBTSxDQUFDLENBQUE7SUFDeEMsQ0FBQztTQUFNLENBQUM7UUFDUDtZQUFTLE1BQU0sQ0FBQyxDQUFBO0lBQ2pCLENBQUM7QUFDRixDQUFDO0FBRUQ7Ozs7O0dBS0c7QUFDSCxNQUFNLFVBQVUsS0FBSztBQUNwQjs7R0FFRztBQUNILEdBQWlCO0lBRWpCLE9BQU8sTUFBTSxDQUFDLE9BQU8sQ0FBQyxHQUFHLENBQWEsQ0FBQTtBQUN2QyxDQUFDO0FBRUQ7Ozs7O0dBS0c7QUFDSCxNQUFNLFNBQVMsQ0FBQyxDQUFDLE1BQU07QUFDdEI7O0dBRUc7QUFDSCxHQUFRO0lBRVIsTUFBTSxHQUFHLEdBQUcsR0FBRyxDQUFDLE1BQU0sQ0FBQTtJQUN0QixLQUFLLElBQUksQ0FBQyxHQUFHLENBQUMsRUFBRSxDQUFDLEdBQUcsR0FBRyxFQUFFLENBQUMsRUFBRSxFQUFFLENBQUM7UUFDOUIsNERBQTREO1FBQzVELE1BQU0sQ0FBQyxDQUFDLEVBQUUsR0FBRyxDQUFDLENBQUMsQ0FBQyxDQUFDLENBQUE7SUFDbEIsQ0FBQztBQUNGLENBQUM7QUFFRDs7OztHQUlHO0FBQ0gsTUFBTSxTQUFTLENBQUMsQ0FBQyxJQUFJO0FBQ3BCOztHQUVHO0FBQ0gsRUFBZTtJQUVmLFNBQVMsQ0FBQztRQUNULE1BQU0sQ0FBQyxHQUFHLEVBQUUsQ0FBQyxJQUFJLEVBQUUsQ0FBQTtRQUNuQixJQUFJLENBQUMsQ0FBQyxJQUFJO1lBQUUsT0FBTTtRQUNsQixNQUFNLENBQUMsQ0FBQyxLQUFLLENBQUE7SUFDZCxDQUFDO0FBQ0YsQ0FBQztBQUVELHFCQUFxQjtBQUVyQixvQkFBb0I7QUFFcEI7Ozs7O0dBS0c7QUFDSCxNQUFNLFNBQVMsQ0FBQyxDQUFDLFFBQVE7QUFDeEI7O0dBRUc7QUFDSCxFQUFlO0FBQ2Y7O0dBRUc7QUFDSCxTQUFZO0lBRVosSUFBSSxLQUFLLEdBQUcsSUFBSSxDQUFBO0lBQ2hCLEtBQUssTUFBTSxDQUFDLElBQUksRUFBRSxFQUFFLENBQUM7UUFDcEIsSUFBSSxDQUFDLEtBQUs7WUFBRSxNQUFNLFNBQVMsQ0FBQTtRQUMzQixNQUFNLENBQUMsQ0FBQTtRQUNQLEtBQUssR0FBRyxLQUFLLENBQUE7SUFDZCxDQUFDO0FBQ0YsQ0FBQztBQUVEOzs7Ozs7O0dBT0c7QUFDSCxNQUFNLFNBQVMsQ0FBQyxDQUFDLFNBQVM7QUFDekI7O0dBRUc7QUFDSCxJQUFpQjtJQUVqQixJQUFJLENBQUMsR0FBRyxDQUFDLENBQUE7SUFDVCxLQUFLLE1BQU0sQ0FBQyxJQUFJLElBQUk7UUFBRSxNQUFNLENBQUMsQ0FBQyxFQUFFLENBQUMsRUFBRSxDQUFDLENBQUE7QUFDckMsQ0FBQztBQTRCRCxNQUFNLFNBQVMsQ0FBQyxDQUFDLE1BQU07QUFDdEI7O0dBRUc7QUFDSCxJQUFpQjtBQUNqQjs7R0FFRztBQUNILElBQXlCO0lBRXpCLEtBQUssTUFBTSxHQUFHLElBQUksSUFBSSxFQUFFLENBQUM7UUFDeEIsSUFBSSxJQUFJLENBQUMsR0FBRyxDQUFDO1lBQUUsTUFBTSxHQUFHLENBQUE7SUFDekIsQ0FBQztBQUNGLENBQUM7QUE0QkQsTUFBTSxTQUFTLENBQUMsQ0FBQyxTQUFTO0FBQ3pCOztHQUVHO0FBQ0gsSUFBaUI7QUFDakI7O0dBRUc7QUFDSCxJQUF5QjtJQUV6QixLQUFLLE1BQU0sR0FBRyxJQUFJLElBQUksRUFBRSxDQUFDO1FBQ3hCLElBQUksQ0FBQyxJQUFJLENBQUMsR0FBRyxDQUFDO1lBQUUsT0FBTTtRQUN0QixNQUFNLEdBQUcsQ0FBQTtJQUNWLENBQUM7QUFDRixDQUFDO0FBRUQ7Ozs7O0dBS0c7QUFDSCxNQUFNLFNBQVMsQ0FBQyxDQUFDLFNBQVM7QUFDekI7O0dBRUc7QUFDSCxJQUFpQjtBQUNqQjs7R0FFRztBQUNILElBQXlCO0lBRXpCLElBQUksTUFBTSxHQUFHLEtBQUssQ0FBQTtJQUNsQixLQUFLLE1BQU0sR0FBRyxJQUFJLElBQUksRUFBRSxDQUFDO1FBQ3hCLElBQUksTUFBTSxJQUFJLENBQUMsSUFBSSxDQUFDLEdBQUcsQ0FBQyxFQUFFLENBQUM7WUFDMUIsTUFBTSxHQUFHLElBQUksQ0FBQTtZQUNiLE1BQU0sR0FBRyxDQUFBO1FBQ1YsQ0FBQztJQUNGLENBQUM7QUFDRixDQUFDO0FBRUQ7Ozs7O0dBS0c7QUFDSCxNQUFNLFNBQVMsQ0FBQyxDQUFDLEdBQUc7QUFDbkI7O0dBRUc7QUFDSCxJQUFpQjtBQUNqQjs7R0FFRztBQUNILFNBQXdCO0lBRXhCLEtBQUssTUFBTSxHQUFHLElBQUksSUFBSTtRQUFFLE1BQU0sU0FBUyxDQUFDLEdBQUcsQ0FBQyxDQUFBO0FBQzdDLENBQUM7QUFFRDs7Ozs7O0dBTUc7QUFDSCxNQUFNLFNBQVMsQ0FBQyxDQUFDLE9BQU87QUFDdkI7O0dBRUc7QUFDSCxJQUFpQjtBQUNqQjs7R0FFRztBQUNILE9BQWdDO0FBQ2hDOztHQUVHO0FBQ0gsT0FBVTtJQUVWLElBQUksS0FBSyxHQUFHLE9BQU8sQ0FBQTtJQUNuQixLQUFLLE1BQU0sR0FBRyxJQUFJLElBQUksRUFBRSxDQUFDO1FBQ3hCLEtBQUssR0FBRyxPQUFPLENBQUMsR0FBRyxFQUFFLEtBQUssQ0FBQyxDQUFBO1FBQzNCLE1BQU0sS0FBSyxDQUFBO0lBQ1osQ0FBQztBQUNGLENBQUM7QUFFRDs7OztHQUlHO0FBQ0gsTUFBTSxTQUFTLENBQUMsQ0FBQyxXQUFXO0FBQzNCOztHQUVHO0FBQ0gsSUFBaUI7QUFDakI7O0dBRUc7QUFDSCxPQUFxQztBQUNyQzs7R0FFRztBQUNILE9BQVU7SUFFVixJQUFJLEtBQUssR0FBRyxPQUFPLENBQUE7SUFDbkIsS0FBSyxNQUFNLEdBQUcsSUFBSSxJQUFJLEVBQUUsQ0FBQztRQUN4QixNQUFNLE9BQU8sR0FBRyxPQUFPLENBQUMsR0FBRyxFQUFFLEtBQUssQ0FBQyxDQUFBO1FBQ25DLEtBQUssR0FBRyxPQUFPLENBQUMsQ0FBQyxDQUFDLENBQUE7UUFDbEIsTUFBTSxPQUFPLENBQUMsQ0FBQyxDQUFDLENBQUE7SUFDakIsQ0FBQztBQUNGLENBQUM7QUFFRDs7Ozs7R0FLRztBQUNILE1BQU0sU0FBUyxDQUFDLENBQUMsS0FBSyxDQUFJLElBQWlCO0lBQzFDLE1BQU0sR0FBRyxHQUFRLEVBQUUsQ0FBQTtJQUNuQixLQUFLLE1BQU0sR0FBRyxJQUFJLElBQUksRUFBRSxDQUFDO1FBQ3hCLEdBQUcsQ0FBQyxJQUFJLENBQUMsR0FBRyxDQUFDLENBQUE7UUFDYixNQUFNLEdBQUcsQ0FBQTtJQUNWLENBQUM7SUFDRCxJQUFJLENBQUMsR0FBRyxDQUFDLE1BQU07UUFBRSxPQUFNO0lBQ3ZCLFNBQVMsQ0FBQztRQUNULEtBQUssTUFBTSxHQUFHLElBQUksR0FBRztZQUFFLE1BQU0sR0FBRyxDQUFBO0lBQ2pDLENBQUM7QUFDRixDQUFDO0FBRUQ7Ozs7O0dBS0c7QUFDSCxNQUFNLFNBQVMsQ0FBQyxDQUFDLE1BQU0sQ0FBSSxJQUFpQixFQUFFLENBQVM7SUFDdEQsSUFBSSxDQUFDLENBQUM7UUFBRSxPQUFNO0lBQ2QsSUFBSSxDQUFDLEtBQUssQ0FBQztRQUFFLE9BQU8sSUFBSSxDQUFBO0lBQ3hCLE1BQU0sR0FBRyxHQUFRLEVBQUUsQ0FBQTtJQUNuQixLQUFLLE1BQU0sR0FBRyxJQUFJLElBQUksRUFBRSxDQUFDO1FBQ3hCLEdBQUcsQ0FBQyxJQUFJLENBQUMsR0FBRyxDQUFDLENBQUE7UUFDYixNQUFNLEdBQUcsQ0FBQTtJQUNWLENBQUM7SUFDRCxDQUFDLEVBQUUsQ0FBQTtJQUNILE9BQU8sQ0FBQyxFQUFFLENBQUM7UUFDVixLQUFLLE1BQU0sR0FBRyxJQUFJLEdBQUc7WUFBRSxNQUFNLEdBQUcsQ0FBQTtRQUNoQyxDQUFDLEVBQUUsQ0FBQTtJQUNKLENBQUM7QUFDRixDQUFDO0FBRUQ7Ozs7O0dBS0c7QUFDSCxNQUFNLFNBQVMsQ0FBQyxDQUFDLElBQUksQ0FBSSxJQUFpQixFQUFFLENBQVM7SUFDcEQsSUFBSSxDQUFDLElBQUksQ0FBQztRQUFFLE9BQU07SUFDbEIsSUFBSSxDQUFDLEdBQUcsQ0FBQyxDQUFBO0lBQ1QsS0FBSyxNQUFNLEdBQUcsSUFBSSxJQUFJLEVBQUUsQ0FBQztRQUN4QixNQUFNLEdBQUcsQ0FBQTtRQUNULENBQUMsRUFBRSxDQUFBO1FBQ0gsSUFBSSxDQUFDLElBQUksQ0FBQztZQUFFLE9BQU07SUFDbkIsQ0FBQztBQUNGLENBQUM7QUFFRDs7Ozs7R0FLRztBQUNILE1BQU0sU0FBUyxDQUFDLENBQUMsSUFBSSxDQUFJLElBQWlCLEVBQUUsQ0FBUztJQUNwRCxJQUFJLENBQUMsSUFBSSxDQUFDO1FBQUUsT0FBTyxJQUFJLENBQUE7SUFDdkIsTUFBTSxFQUFFLEdBQUcsSUFBSSxDQUFDLE1BQU0sQ0FBQyxRQUFRLENBQUMsRUFBRSxDQUFBO0lBQ2xDLEtBQUssSUFBSSxDQUFDLEdBQUcsQ0FBQyxFQUFFLENBQUMsR0FBRyxDQUFDLEVBQUUsQ0FBQyxFQUFFLEVBQUUsQ0FBQztRQUM1QixNQUFNLEdBQUcsR0FBRyxFQUFFLENBQUMsSUFBSSxFQUFFLENBQUE7UUFDckIsSUFBSSxHQUFHLENBQUMsSUFBSTtZQUFFLE9BQU07SUFDckIsQ0FBQztJQUNELFNBQVMsQ0FBQztRQUNULE1BQU0sR0FBRyxHQUFHLEVBQUUsQ0FBQyxJQUFJLEVBQUUsQ0FBQTtRQUNyQixJQUFJLEdBQUcsQ0FBQyxJQUFJO1lBQUUsT0FBTTtRQUNwQixNQUFNLEdBQUcsQ0FBQyxLQUFLLENBQUE7SUFDaEIsQ0FBQztBQUNGLENBQUM7QUFFRDs7Ozs7Ozs7R0FRRztBQUNILE1BQU0sU0FBUyxDQUFDLENBQUMsS0FBSyxDQUFJLElBQWlCLEVBQUUsQ0FBUztJQUNyRCxJQUFJLEtBQUssR0FBUSxFQUFFLENBQUE7SUFDbkIsSUFBSSxDQUFDLEdBQUcsQ0FBQyxDQUFBO0lBQ1QsS0FBSyxNQUFNLENBQUMsSUFBSSxJQUFJLEVBQUUsQ0FBQztRQUN0QixLQUFLLENBQUMsSUFBSSxDQUFDLENBQUMsQ0FBQyxDQUFBO1FBQ2IsQ0FBQyxFQUFFLENBQUE7UUFDSCxJQUFJLENBQUMsS0FBSyxDQUFDLEVBQUUsQ0FBQztZQUNiLE1BQU0sS0FBSyxDQUFBO1lBQ1gsS0FBSyxHQUFHLEVBQUUsQ0FBQTtZQUNWLENBQUMsR0FBRyxDQUFDLENBQUE7UUFDTixDQUFDO0lBQ0YsQ0FBQztJQUNELElBQUksQ0FBQztRQUFFLE1BQU0sS0FBSyxDQUFBO0FBQ25CLENBQUM7QUFFRDs7Ozs7R0FLRztBQUNILE1BQU0sU0FBUyxDQUFDLENBQUMsTUFBTTtBQUN0Qjs7R0FFRztBQUNILElBQWlCO0FBQ2pCOztHQUVHO0FBQ0gsV0FBbUI7QUFDbkI7O0dBRUc7QUFDSCxVQUFrQjtJQUVsQixNQUFNLE1BQU0sR0FBUSxFQUFFLENBQUE7SUFDdEIsZ0JBQWdCO0lBQ2hCLElBQUksT0FBTyxHQUFNLEtBQUssQ0FBTSxDQUFBO0lBQzVCLE1BQU0sS0FBSyxHQUFRLEVBQUUsQ0FBQTtJQUNyQixJQUFJLFNBQVMsR0FBRyxDQUFDLENBQUE7SUFDakIsSUFBSSxRQUFRLEdBQUcsQ0FBQyxDQUFBO0lBQ2hCLElBQUksVUFBVSxHQUFHLEtBQUssQ0FBQTtJQUV0QixLQUFLLE1BQU0sQ0FBQyxJQUFJLElBQUksRUFBRSxDQUFDO1FBQ3RCLEtBQUssQ0FBQyxJQUFJLENBQUMsQ0FBQyxDQUFDLENBQUE7UUFDYixRQUFRLEVBQUUsQ0FBQTtRQUNWLElBQUksUUFBUSxHQUFHLFVBQVUsRUFBRSxDQUFDO1lBQzNCLElBQUksVUFBVSxFQUFFLENBQUM7Z0JBQ2hCLE1BQU0sQ0FBQyxJQUFJLENBQUMsT0FBTyxDQUFDLENBQUE7Z0JBQ3BCLFNBQVMsRUFBRSxDQUFBO1lBQ1osQ0FBQztZQUNELElBQUksU0FBUyxHQUFHLFdBQVcsRUFBRSxDQUFDO2dCQUM3QixNQUFNLENBQUMsS0FBSyxFQUFFLENBQUE7Z0JBQ2QsU0FBUyxFQUFFLENBQUE7WUFDWixDQUFDO1lBQ0QsT0FBTyxHQUFHLEtBQUssQ0FBQyxLQUFLLEVBQUcsQ0FBQTtZQUN4QixRQUFRLEVBQUUsQ0FBQTtZQUNWLFVBQVUsR0FBRyxJQUFJLENBQUE7WUFDakIsTUFBTSxDQUFDLENBQUMsR0FBRyxNQUFNLENBQUMsRUFBRSxPQUFPLEVBQUUsQ0FBQyxHQUFHLEtBQUssQ0FBQyxDQUFDLENBQUE7UUFDekMsQ0FBQztJQUNGLENBQUM7SUFDRCxPQUFPLFFBQVEsRUFBRSxDQUFDO1FBQ2pCLElBQUksVUFBVSxFQUFFLENBQUM7WUFDaEIsTUFBTSxDQUFDLElBQUksQ0FBQyxPQUFPLENBQUMsQ0FBQTtZQUNwQixTQUFTLEVBQUUsQ0FBQTtRQUNaLENBQUM7UUFDRCxJQUFJLFNBQVMsR0FBRyxXQUFXLEVBQUUsQ0FBQztZQUM3QixNQUFNLENBQUMsS0FBSyxFQUFFLENBQUE7WUFDZCxTQUFTLEVBQUUsQ0FBQTtRQUNaLENBQUM7UUFDRCxPQUFPLEdBQUcsS0FBSyxDQUFDLEtBQUssRUFBRyxDQUFBO1FBQ3hCLFFBQVEsRUFBRSxDQUFBO1FBQ1YsVUFBVSxHQUFHLElBQUksQ0FBQTtRQUNqQixNQUFNLENBQUMsQ0FBQyxHQUFHLE1BQU0sQ0FBQyxFQUFFLE9BQU8sRUFBRSxDQUFDLEdBQUcsS0FBSyxDQUFDLENBQUMsQ0FBQTtJQUN6QyxDQUFDO0FBQ0YsQ0FBQztBQUVEOzs7OztHQUtHO0FBQ0gsTUFBTSxTQUFTLENBQUMsQ0FBQyxPQUFPO0FBQ3ZCOztHQUVHO0FBQ0gsSUFBaUI7QUFDakI7O0dBRUc7QUFDSCxTQUFrQztJQUVsQyxLQUFLLE1BQU0sRUFBRSxJQUFJLElBQUksRUFBRSxDQUFDO1FBQ3ZCLEtBQUssTUFBTSxHQUFHLElBQUksU0FBUyxDQUFDLEVBQUUsQ0FBQyxFQUFFLENBQUM7WUFDakMsTUFBTSxHQUFHLENBQUE7UUFDVixDQUFDO0lBQ0YsQ0FBQztBQUNGLENBQUM7QUFFRDs7OztHQUlHO0FBQ0gsTUFBTSxTQUFTLENBQUMsQ0FBQyxLQUFLO0FBQ3JCOztHQUVHO0FBQ0gsSUFBaUI7SUFFakIsTUFBTSxJQUFJLEdBQUcsSUFBSSxHQUFHLEVBQUssQ0FBQTtJQUN6QixLQUFLLE1BQU0sSUFBSSxJQUFJLElBQUksRUFBRSxDQUFDO1FBQ3pCLElBQUksSUFBSSxDQUFDLEdBQUcsQ0FBQyxJQUFJLENBQUM7WUFBRSxTQUFRO1FBQzVCLElBQUksQ0FBQyxHQUFHLENBQUMsSUFBSSxDQUFDLENBQUE7UUFDZCxNQUFNLElBQUksQ0FBQTtJQUNYLENBQUM7QUFDRixDQUFDO0FBMkJELE1BQU0sU0FBUyxDQUFDLENBQUMsT0FBTyxDQUN2QixJQUFpQixFQUNqQixRQUEwQztJQUUxQyxNQUFNLElBQUksR0FBRyxJQUFJLEdBQUcsRUFBVyxDQUFBO0lBQy9CLElBQUksT0FBTyxRQUFRLEtBQUssVUFBVSxFQUFFLENBQUM7UUFDcEMsS0FBSyxNQUFNLElBQUksSUFBSSxJQUFJLEVBQUUsQ0FBQztZQUN6QixNQUFNLE1BQU0sR0FBRyxRQUFRLENBQUMsSUFBSSxDQUFDLENBQUE7WUFDN0IsSUFBSSxJQUFJLENBQUMsR0FBRyxDQUFDLE1BQU0sQ0FBQztnQkFBRSxTQUFRO1lBQzlCLElBQUksQ0FBQyxHQUFHLENBQUMsTUFBTSxDQUFDLENBQUE7WUFDaEIsTUFBTSxJQUFJLENBQUE7UUFDWCxDQUFDO0lBQ0YsQ0FBQztTQUFNLENBQUM7UUFDUCxLQUFLLE1BQU0sSUFBSSxJQUFJLElBQUksRUFBRSxDQUFDO1lBQ3pCLDREQUE0RDtZQUM1RCxNQUFNLEdBQUcsR0FBRyxJQUFJLENBQUMsUUFBUSxDQUFDLENBQUE7WUFDMUIsSUFBSSxJQUFJLENBQUMsR0FBRyxDQUFDLEdBQUcsQ0FBQztnQkFBRSxTQUFRO1lBQzNCLElBQUksQ0FBQyxHQUFHLENBQUMsR0FBRyxDQUFDLENBQUE7WUFDYixNQUFNLElBQUksQ0FBQTtRQUNYLENBQUM7SUFDRixDQUFDO0FBQ0YsQ0FBQztBQUVELHVCQUF1QjtBQUV2QixxQkFBcUI7QUFFckI7Ozs7Ozs7R0FPRztBQUNILE1BQU0sU0FBUyxDQUFDLENBQUMsV0FBVztBQUMzQjs7R0FFRztBQUNILEdBQUcsS0FBb0I7SUFFdkIsTUFBTSxFQUFFLEdBQUcsS0FBSyxDQUFDLEdBQUcsQ0FBQyxDQUFDLENBQUMsRUFBRSxDQUFDLENBQUMsQ0FBQyxNQUFNLENBQUMsUUFBUSxDQUFDLEVBQUUsQ0FBQyxDQUFBO0lBQy9DLE1BQU0sQ0FBQyxHQUEyQixLQUFLLENBQUMsSUFBSSxDQUFDLEVBQUUsTUFBTSxFQUFFLEVBQUUsQ0FBQyxNQUFNLEVBQUUsQ0FBQyxDQUFBO0lBQ25FLFNBQVMsQ0FBQztRQUNULEtBQUssTUFBTSxDQUFDLENBQUMsRUFBRSxPQUFPLENBQUMsSUFBSSxFQUFFLENBQUMsT0FBTyxFQUFFLEVBQUUsQ0FBQztZQUN6Qyw0REFBNEQ7WUFDNUQsQ0FBQyxDQUFDLENBQUMsQ0FBQyxHQUFHLE9BQU8sQ0FBQyxJQUFJLEVBQUUsQ0FBQTtZQUNyQiw0REFBNEQ7WUFDNUQsSUFBSSxDQUFDLENBQUMsQ0FBQyxDQUFDLENBQUMsSUFBSTtnQkFBRSxPQUFNO1FBQ3RCLENBQUM7UUFDRCxLQUFLLE1BQU0sQ0FBQyxJQUFJLENBQUM7WUFBRSxNQUFNLENBQUMsQ0FBQyxLQUFLLENBQUE7SUFDakMsQ0FBQztBQUNGLENBQUM7QUFtQ0Q7Ozs7Ozs7R0FPRztBQUNILE1BQU0sU0FBUyxDQUFDLENBQUMsR0FBRyxDQUFJLEdBQUcsS0FBb0I7SUFDOUMsTUFBTSxFQUFFLEdBQUcsS0FBSyxDQUFDLEdBQUcsQ0FBQyxDQUFDLENBQUMsRUFBRSxDQUFDLENBQUMsQ0FBQyxNQUFNLENBQUMsUUFBUSxDQUFDLEVBQUUsQ0FBQyxDQUFBO0lBRS9DLE1BQU0sQ0FBQyxHQUEyQixLQUFLLENBQUMsSUFBSSxDQUFDLEVBQUUsTUFBTSxFQUFFLEVBQUUsQ0FBQyxNQUFNLEVBQUUsQ0FBQyxDQUFBO0lBQ25FLFNBQVMsQ0FBQztRQUNULEtBQUssTUFBTSxDQUFDLENBQUMsRUFBRSxPQUFPLENBQUMsSUFBSSxFQUFFLENBQUMsT0FBTyxFQUFFLEVBQUUsQ0FBQztZQUN6Qyw0REFBNEQ7WUFDNUQsQ0FBQyxDQUFDLENBQUMsQ0FBQyxHQUFHLE9BQU8sQ0FBQyxJQUFJLEVBQUUsQ0FBQTtZQUNyQiw0REFBNEQ7WUFDNUQsSUFBSSxDQUFDLENBQUMsQ0FBQyxDQUFDLENBQUMsSUFBSTtnQkFBRSxPQUFNO1FBQ3RCLENBQUM7UUFDRCxNQUFNLENBQUMsQ0FBQyxHQUFHLENBQUMsQ0FBQyxDQUFDLEVBQUUsQ0FBQyxDQUFDLENBQUMsS0FBSyxDQUFDLENBQUE7SUFDMUIsQ0FBQztBQUNGLENBQUM7QUFFRDs7Ozs7Ozs7R0FRRztBQUNILE1BQU0sU0FBUyxDQUFDLENBQUMsS0FBSyxDQUFJLEdBQUcsS0FBb0I7SUFDaEQsS0FBSyxNQUFNLElBQUksSUFBSSxLQUFLO1FBQUUsS0FBSyxNQUFNLEdBQUcsSUFBSSxJQUFJO1lBQUUsTUFBTSxHQUFHLENBQUE7QUFDNUQsQ0FBQztBQUNEOzs7Ozs7OztHQVFHO0FBQ0gsTUFBTSxTQUFTLENBQUMsQ0FBQyxPQUFPLENBQUksS0FBNEI7SUFDdkQsS0FBSyxNQUFNLElBQUksSUFBSSxLQUFLO1FBQUUsS0FBSyxNQUFNLEdBQUcsSUFBSSxJQUFJO1lBQUUsTUFBTSxHQUFHLENBQUE7QUFDNUQsQ0FBQztBQUVELHdCQUF3QjtBQUV4QixpQkFBaUI7QUFFakI7Ozs7OztHQU1HO0FBQ0gsTUFBTSxVQUFVLElBQUk7QUFDbkI7O0dBRUc7QUFDSCxRQUFxQjtBQUNyQjs7OztHQUlHO0FBQ0gsQ0FBQyxHQUFHLENBQUM7SUFFTCxJQUFJLENBQUMsS0FBSyxDQUFDO1FBQUUsT0FBTyxDQUFDLEVBQUUsRUFBRSxRQUFRLENBQUMsQ0FBQTtJQUVsQyxNQUFNLEVBQUUsR0FBRyxRQUFRLENBQUMsTUFBTSxDQUFDLFFBQVEsQ0FBQyxFQUFFLENBQUE7SUFDdEMsTUFBTSxNQUFNLEdBQVEsRUFBRSxDQUFBO0lBRXRCLEtBQUssSUFBSSxDQUFDLEdBQUcsQ0FBQyxFQUFFLENBQUMsR0FBRyxDQUFDLEVBQUUsQ0FBQyxFQUFFLEVBQUUsQ0FBQztRQUM1QixNQUFNLENBQUMsR0FBRyxFQUFFLENBQUMsSUFBSSxFQUFFLENBQUE7UUFDbkIsSUFBSSxDQUFDLENBQUMsSUFBSSxFQUFFLENBQUM7WUFDWixPQUFPLENBQUMsTUFBTSxFQUFFLE1BQU0sQ0FBQyxDQUFBO1FBQ3hCLENBQUM7UUFDRCxNQUFNLENBQUMsSUFBSSxDQUFDLENBQUMsQ0FBQyxLQUFLLENBQUMsQ0FBQTtJQUNyQixDQUFDO0lBRUQsT0FBTyxDQUFDLE1BQU0sRUFBRSxLQUFLLENBQUMsTUFBTSxFQUFFLElBQUksQ0FBQyxFQUFFLENBQUMsQ0FBQyxDQUFDLENBQUE7QUFDekMsQ0FBQztBQUVEOzs7OztHQUtHO0FBQ0gsTUFBTSxVQUFVLFFBQVE7QUFDdkI7O0dBRUc7QUFDSCxJQUFpQjtJQUVqQixnQkFBZ0I7SUFDaEIsSUFBSSxLQUFLLEdBQThCLENBQUMsS0FBSyxDQUFNLEVBQUUsS0FBSyxDQUFDLENBQUE7SUFDM0QsTUFBTSxFQUFFLEdBQUcsSUFBSSxDQUFDLE1BQU0sQ0FBQyxRQUFRLENBQUMsRUFBRSxDQUFBO0lBRWxDLFNBQVMsT0FBTztRQUNmLElBQUksS0FBSyxDQUFDLENBQUMsQ0FBQztZQUFFLE9BQU07UUFDcEIsTUFBTSxDQUFDLEdBQUcsRUFBRSxDQUFDLElBQUksRUFBRSxDQUFBO1FBQ25CLElBQUksQ0FBQyxDQUFDLElBQUk7WUFBRSxLQUFLLEdBQUcsQ0FBQyxJQUFJLEVBQUUsSUFBSSxDQUFDLENBQUE7O1lBQzNCLEtBQUssR0FBRyxDQUFDLENBQUMsQ0FBQyxLQUFLLEVBQUUsS0FBSyxDQUFDLENBQUE7SUFDOUIsQ0FBQztJQUNELE9BQU8sRUFBRSxDQUFBO0lBRVQsT0FBTztRQUNOLENBQUMsTUFBTSxDQUFDLFFBQVEsQ0FBQztZQUNoQixPQUFPO2dCQUNOLElBQUk7b0JBQ0gsSUFBSSxLQUFLLENBQUMsQ0FBQyxDQUFDO3dCQUFFLE9BQU8sRUFBRSxJQUFJLEVBQUUsSUFBSSxFQUFFLEtBQUssRUFBRSxLQUFLLENBQUMsRUFBRSxDQUFBO29CQUNsRCxNQUFNLEtBQUssR0FBRyxLQUFLLENBQUMsQ0FBQyxDQUFDLENBQUE7b0JBQ3RCLE9BQU8sRUFBRSxDQUFBO29CQUNULE9BQU8sRUFBRSxLQUFLLEVBQUUsQ0FBQTtnQkFDakIsQ0FBQzthQUNELENBQUE7UUFDRixDQUFDO1FBQ0QsSUFBSSxJQUFJO1lBQ1AsT0FBTyxLQUFLLENBQUMsQ0FBQyxDQUFDLENBQUE7UUFDaEIsQ0FBQztRQUNELElBQUk7WUFDSCxJQUFJLEtBQUssQ0FBQyxDQUFDLENBQUM7Z0JBQUUsTUFBTSxJQUFJLEtBQUssQ0FBQyxnQkFBZ0IsQ0FBQyxDQUFBO1lBQy9DLE9BQU8sS0FBSyxDQUFDLENBQUMsQ0FBQyxDQUFBO1FBQ2hCLENBQUM7S0FDRCxDQUFBO0FBQ0YsQ0FBQztBQUVELG9CQUFvQjtBQUVwQixvQkFBb0I7QUFFcEI7Ozs7O0dBS0c7QUFDSCxNQUFNLFVBQVUsTUFBTTtBQUNyQjs7R0FFRztBQUNILElBQWlCO0FBQ2pCOztHQUVHO0FBQ0gsT0FBZ0M7QUFDaEM7O0dBRUc7QUFDSCxPQUFVO0lBRVYsSUFBSSxLQUFLLEdBQUcsT0FBTyxDQUFBO0lBQ25CLEtBQUssTUFBTSxHQUFHLElBQUksSUFBSTtRQUFFLEtBQUssR0FBRyxPQUFPLENBQUMsR0FBRyxFQUFFLEtBQUssQ0FBQyxDQUFBO0lBQ25ELE9BQU8sS0FBSyxDQUFBO0FBQ2IsQ0FBQztBQWtDRCxNQUFNLFVBQVUsSUFBSTtBQUNuQjs7R0FFRztBQUNILElBQWlCO0FBQ2pCOztHQUVHO0FBQ0gsU0FBOEI7QUFDOUI7O0dBRUc7QUFDSCxRQUFZO0lBRVosS0FBSyxNQUFNLEdBQUcsSUFBSSxJQUFJO1FBQUUsSUFBSSxTQUFTLENBQUMsR0FBRyxDQUFDO1lBQUUsT0FBTyxHQUFHLENBQUE7SUFDdEQsT0FBTyxRQUFRLENBQUE7QUFDaEIsQ0FBQztBQUVEOzs7OztHQUtHO0FBQ0gsTUFBTSxVQUFVLElBQUk7QUFDbkI7O0dBRUc7QUFDSCxJQUFpQjtBQUNqQjs7R0FFRztBQUNILFNBQThCO0lBRTlCLEtBQUssTUFBTSxHQUFHLElBQUksSUFBSTtRQUFFLElBQUksU0FBUyxDQUFDLEdBQUcsQ0FBQztZQUFFLE9BQU8sSUFBSSxDQUFBO0lBQ3ZELE9BQU8sS0FBSyxDQUFBO0FBQ2IsQ0FBQztBQUVEOzs7OztHQUtHO0FBQ0gsTUFBTSxVQUFVLEtBQUs7QUFDcEI7O0dBRUc7QUFDSCxJQUFpQjtBQUNqQjs7R0FFRztBQUNILFNBQThCO0lBRTlCLEtBQUssTUFBTSxHQUFHLElBQUksSUFBSTtRQUFFLElBQUksQ0FBQyxTQUFTLENBQUMsR0FBRyxDQUFDO1lBQUUsT0FBTyxLQUFLLENBQUE7SUFDekQsT0FBTyxJQUFJLENBQUE7QUFDWixDQUFDO0FBRUQ7Ozs7O0dBS0c7QUFDSCxNQUFNLFVBQVUsTUFBTTtBQUNyQjs7R0FFRztBQUNILElBQXVCO0lBRXZCLElBQUksS0FBSyxDQUFDLE9BQU8sQ0FBQyxJQUFJLENBQUM7UUFBRSxPQUFPLElBQUksQ0FBQyxNQUFNLENBQUE7SUFDM0MsSUFBSSxDQUFDLEdBQUcsQ0FBQyxDQUFBO0lBQ1QsS0FBSyxNQUFNLENBQUMsSUFBSSxJQUFJLEVBQUUsQ0FBQztRQUN0QixLQUFLLENBQUMsQ0FBQTtRQUNOLENBQUMsRUFBRSxDQUFBO0lBQ0osQ0FBQztJQUNELE9BQU8sQ0FBQyxDQUFBO0FBQ1QsQ0FBQztBQUVEOzs7O0dBSUc7QUFDSCxNQUFNLFVBQVUsT0FBTztBQUN0Qjs7R0FFRztBQUNILElBQWlCO0FBQ2pCOztHQUVHO0FBQ0gsRUFBb0I7SUFFcEIsS0FBSyxNQUFNLEdBQUcsSUFBSSxJQUFJO1FBQUUsRUFBRSxDQUFDLEdBQUcsQ0FBQyxDQUFBO0FBQ2hDLENBQUM7QUFFRDs7OztHQUlHO0FBQ0gsTUFBTSxVQUFVLE9BQU87QUFDdEI7O0dBRUc7QUFDSCxJQUFpQjtJQUVqQixPQUFPLENBQUMsR0FBRyxJQUFJLENBQUMsQ0FBQTtBQUNqQixDQUFDO0FBRUQsdUJBQXVCIn0=